'use client'

import { type ReactNode, Suspense, useEffect, useState } from 'react'

import dynamic from 'next/dynamic'

import { useTranslation } from '@/hooks/client/use-translation'
import type { StoryMetaV1 } from '@/types/shared-types'

// Keep the initial server-rendered article grid synchronous. Only the mobile
// enhancement needs the carousel module, after the viewport is known.
const ArticleGridCarousel = dynamic(() =>
  import('./article-grid-carousel').then((module) => module.ArticleGridCarousel)
)

const ArrowDownIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 384 512" className="h-4 w-4" fill="currentColor">
    <path d="M169.4 470.6c12.5 12.5 32.8 12.5 45.3 0l160-160c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0L224 370.8V64c0-17.7-14.3-32-32-32s-32 14.3-32 32v306.7L54.6 265.4c-12.5-12.5-32.8-12.5-45.3 0s-12.5 32.8 0 45.3l160 160z" />
  </svg>
)

interface ArticleGridClientProps {
  title?: string
  initialArticles: ReactNode
  remainingArticles: ReactNode | null
  allArticles: StoryMetaV1[]
  totalCount: number
  initialCount: number
  disableCarousel?: boolean
  seoLinks: ReactNode
}

export const ArticleGridClient = ({
  title,
  initialArticles,
  remainingArticles,
  allArticles,
  totalCount,
  initialCount,
  disableCarousel,
  seoLinks,
}: ArticleGridClientProps) => {
  const { t } = useTranslation()
  const [showAll, setShowAll] = useState(false)
  const [isMobile, setIsMobile] = useState(false)
  const [hasMounted, setHasMounted] = useState(false)
  const hasMore = totalCount > initialCount
  const showCarousel = hasMounted && isMobile && !showAll && !disableCarousel

  useEffect(() => {
    setHasMounted(true)
    const check = () => setIsMobile(window.innerWidth <= 767)
    check()
    window.addEventListener('resize', check)
    return () => window.removeEventListener('resize', check)
  }, [])

  return (
    <section className="n4-article-grid max-xs:gap-8 mx-auto mt-2 mb-20 flex w-full max-w-[1344px] flex-col self-stretch">
      {title && (
        <span className="grid-title max-xs:mb-0 max-xs:w-full max-xs:text-[1.75rem] max-xs:leading-[2.275rem] max-xs:tracking-[-0.0175rem] mb-8 text-[2rem] leading-[2.4rem] font-[450] tracking-[-0.02rem] text-black">
          {title}
        </span>
      )}
      <section className="article-content relative flex flex-col">
        <section className="cards laptop:grid-cols-3 grid gap-8 md:grid-cols-2">
          {showCarousel ? (
            <Suspense fallback={initialArticles}>
              <ArticleGridCarousel articles={allArticles} />
            </Suspense>
          ) : (
            <>
              {initialArticles}
              {showAll && remainingArticles}
            </>
          )}
        </section>
      </section>
      {(hasMore || (isMobile && !disableCarousel)) && !showAll && (
        <button
          type="button"
          aria-expanded={showAll}
          data-ht-disclosure-trigger="read-more"
          data-tracking="ignore"
          className="action max-xs:mt-0 mt-20 flex cursor-pointer items-center justify-center gap-4"
          onClick={() => setShowAll(true)}
        >
          <span className="text text-navy cursor-pointer text-[1.125rem] leading-6.75 font-[350] tracking-[-0.01125rem]">
            {t('client.articleGrid.showAll', 'Show all articles')}
          </span>
          <span className="show border-navy-20 relative flex h-12 w-12 cursor-pointer items-center justify-center rounded-full border-[0.889px] bg-transparent">
            <span className="text-navy">
              <ArrowDownIcon />
            </span>
          </span>
        </button>
      )}
      <ul className="hidden">{seoLinks}</ul>
    </section>
  )
}
